import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, ProgressBar, Button, Form, InputGroup } from 'react-bootstrap';
import SidebarAdmin from './SidebarAdmin';

const JOURS_EN_MS = 7 * 24 * 60 * 60 * 1000;
const currentYear = new Date().getFullYear();

function CartePersonnage({ id, nom, icone, variante }) {
  // 1. Charger l'état actif (Go) depuis le localStorage
  const [estActif, setEstActif] = useState(() => {
    const sauvegardeActif = localStorage.getItem(`totalbattle_active_${id}`);
    return sauvegardeActif === 'true';
  });

  // 2. Charger le temps restant en soustrayant le temps écoulé si le minuteur tournait
  const [tempsRestant, setTempsRestant] = useState(() => {
    const sauvegardeTemps = localStorage.getItem(`totalbattle_timer_${id}`);
    const sauvegardeDate = localStorage.getItem(`totalbattle_timestamp_${id}`);
    const initialTemps = sauvegardeTemps ? parseInt(sauvegardeTemps, 10) : JOURS_EN_MS;

    // Si le minuteur était actif, on calcule combien de temps s'est écoulé pendant l'absence
    const sauvegardeActif = localStorage.getItem(`totalbattle_active_${id}`) === 'true';
    if (sauvegardeActif && sauvegardeDate) {
      const tempsEcoulePendantAbsence = Date.now() - parseInt(sauvegardeDate, 10);
      const tempsAjuste = initialTemps - tempsEcoulePendantAbsence;
      return tempsAjuste > 0 ? tempsAjuste : 0;
    }

    return initialTemps;
  });

  // Gestion du niveau (Sauvegardé de 1 à 500)
  const [niveau, setNiveau] = useState(() => {
    const sauvegardeNiveau = localStorage.getItem(`totalbattle_level_${id}`);
    return sauvegardeNiveau ? parseInt(sauvegardeNiveau, 10) : 1;
  });

  const [modeEdition, setModeEdition] = useState(false);
  const [editJ, setEditJ] = useState(7);
  const [editH, setEditH] = useState(0);
  const [editM, setEditM] = useState(0);
  const [editS, setEditS] = useState(0);

  const intervalleRef = useRef(null);

  // Sauvegarder l'état (Go/Pause) et l'instant T à chaque changement
  useEffect(() => {
    localStorage.setItem(`totalbattle_active_${id}`, estActif.toString());
    localStorage.setItem(`totalbattle_timer_${id}`, tempsRestant.toString());
    localStorage.setItem(`totalbattle_timestamp_${id}`, Date.now().toString());
    localStorage.setItem(`totalbattle_level_${id}`, niveau.toString());
  }, [tempsRestant, estActif, niveau, id]);

  // Boucle du compte à rebours
  useEffect(() => {
    if (estActif && !modeEdition) {
      intervalleRef.current = setInterval(() => {
        setTempsRestant((prev) => {
          if (prev <= 1000) {
            clearInterval(intervalleRef.current);
            setEstActif(false);
            return 0;
          }
          return prev - 1000;
        });
      }, 1000);
    } else {
      clearInterval(intervalleRef.current);
    }
    return () => clearInterval(intervalleRef.current);
  }, [estActif, modeEdition]);

  // Synchronisation des inputs
  useEffect(() => {
    if (!modeEdition) {
      setEditJ(Math.floor(tempsRestant / (1000 * 60 * 60 * 24)));
      setEditH(Math.floor((tempsRestant / (1000 * 60 * 60)) % 24));
      setEditM(Math.floor((tempsRestant / 1000 / 60) % 60));
      setEditS(Math.floor((tempsRestant / 1000) % 60));
    }
  }, [tempsRestant, modeEdition]);

  const appliquerChangements = () => {
    const totalMs =
      (Math.max(0, editJ) * 24 * 60 * 60 * 1000) +
      (Math.min(23, Math.max(0, editH)) * 60 * 60 * 1000) +
      (Math.min(59, Math.max(0, editM)) * 60 * 1000) +
      (Math.min(59, Math.max(0, editS)) * 1000);

    setTempsRestant(totalMs);
    setModeEdition(false);
  };

  // Fonctions pour le compteur de niveau (Bornes 1 - 500)
  const ajusterNiveau = (valeur) => {
    const nouveauNiveau = Math.max(1, Math.min(500, valeur));
    setNiveau(nouveauNiveau);
  };

  const j = Math.floor(tempsRestant / (1000 * 60 * 60 * 24));
  const h = Math.floor((tempsRestant / (1000 * 60 * 60)) % 24);
  const m = Math.floor((tempsRestant / 1000 / 60) % 60);
  const s = Math.floor((tempsRestant / 1000) % 60);
  const pourcentage = ((JOURS_EN_MS - tempsRestant) / JOURS_EN_MS) * 100;

  return (
    <Card variant="dark" className="bg-dark border-secondary text-white h-100 shadow">
      <Card.Body className="d-flex flex-column justify-content-between">

        <div className="d-flex justify-content-between align-items-center mb-3">
          <Card.Title className="fs-5 fw-bold mb-0 d-flex align-items-center gap-2">
            <span>{icone}</span> {nom}
          </Card.Title>

          {modeEdition ? (
            <Button variant="success" size="sm" onClick={appliquerChangements} className="py-0 px-2 d-flex align-items-center gap-1 text-dark fw-bold">
              <i className="material-icons fs-6">check</i> Valider
            </Button>
          ) : (
              <Button variant="outline-secondary" size="sm" onClick={() => { setEstActif(false); setModeEdition(true); }} disabled={estActif} className="py-0 px-2 text-white-50 d-flex align-items-center gap-1">
                <i className="material-icons fs-6">edit</i> Éditer
            </Button>
            )}
        </div>

        <div className="d-flex justify-content-around align-items-center text-center my-3 bg-black bg-opacity-20 py-2 rounded border border-secondary border-opacity-10">
          <div>
            {modeEdition ? (
              <Form.Control
                type="number"
                min="0"
                value={editJ}
                onChange={(e) => setEditJ(Math.max(0, parseInt(e.target.value) || 0))}
                className="bg-secondary text-center fw-bold fs-4 text-white p-1 border-0 rounded"
                style={{ width: '50px' }}
              />
            ) : (
                <div className="fs-3 fw-bold text-white">{j}</div>
              )}
            <small className="text-light fs-7 d-block opacity-50 mt-0">Jours</small>
          </div>

          <div className="fs-3 fw-bold text-secondary mb-4">:</div>

          <div>
            {modeEdition ? (
              <Form.Control
                type="number"
                min="0"
                max="23"
                value={editH}
                onChange={(e) => setEditH(Math.min(23, Math.max(0, parseInt(e.target.value) || 0)))}
                className={`bg-secondary text-center fw-bold fs-4 text-${variante} p-1 border-0 rounded`}
                style={{ width: '50px' }}
              />
            ) : (
                <div className={`fs-3 fw-bold text-${variante}`}>{String(h).padStart(2, '0')}</div>
              )}
            <small className="text-light fs-7 d-block opacity-50 mt-0">Heures</small>
          </div>

          <div className="fs-3 fw-bold text-secondary mb-4">:</div>

          <div>
            {modeEdition ? (
              <Form.Control
                type="number"
                min="0"
                max="59"
                value={editM}
                onChange={(e) => setEditM(Math.min(59, Math.max(0, parseInt(e.target.value) || 0)))}
                className={`bg-secondary text-center fw-bold fs-4 text-${variante} p-1 border-0 rounded`}
                style={{ width: '50px' }}
              />
            ) : (
                <div className={`fs-3 fw-bold text-${variante}`}>{String(m).padStart(2, '0')}</div>
              )}
            <small className="text-light fs-7 d-block opacity-50 mt-0">Min</small>
          </div>

          <div className="fs-3 fw-bold text-secondary mb-4">:</div>

          <div>
            {modeEdition ? (
              <Form.Control
                type="number"
                min="0"
                max="59"
                value={editS}
                onChange={(e) => setEditS(Math.min(59, Math.max(0, parseInt(e.target.value) || 0)))}
                className="bg-secondary text-center fw-bold fs-4 text-warning p-1 border-0 rounded"
                style={{ width: '50px' }}
              />
            ) : (
                <div className="fs-3 fw-bold text-warning">{String(s).padStart(2, '0')}</div>
              )}
            <small className="text-light fs-7 d-block opacity-50 mt-0 ">Sec</small>
          </div>
        </div>

        <div className="mb-4">
          <p className="text-center text-info mt-0 mb-2">Bouclier de la paix</p>
          <div className="d-flex justify-content-between small text-light mb-1">
            <span>Progression</span>
            <span>{Math.max(0, Math.min(100, Math.round(pourcentage)))}%</span>
          </div>
          <ProgressBar variant={variante} now={pourcentage} className="bg-secondary" style={{ height: '6px' }} />
        </div>

        {/* --- NOUVEAU COMPTEUR DE NIVEAU (1 à 500) --- */}
        <div className="bg-black bg-opacity-30 p-2 rounded mb-3 border border-secondary border-opacity-10 text-center">
          <label className="small fw-semibold text-white-50 mb-1 d-block text-uppercase" style={{ letterSpacing: '0.5px' }}>
            Niveau du Héros
          </label>
          <InputGroup size="sm" className="justify-content-center mx-auto" style={{ maxWidth: '140px' }}>
            <Button
              variant="outline-secondary"
              className="text-white px-2"
              onClick={() => ajusterNiveau(niveau - 1)}
              disabled={niveau <= 1}
            >
              <i className="material-icons fs-6 d-flex align-items-center">remove</i>
            </Button>

            <Form.Control
              type="number"
              min="1"
              max="500"
              value={niveau}
              onChange={(e) => ajusterNiveau(parseInt(e.target.value) || 1)}
              className="bg-dark text-white text-center fw-bold border-secondary fs-6 p-0"
              style={{ boxShadow: 'none' }}
            />

            <Button
              variant="outline-secondary"
              className="text-white px-2"
              onClick={() => ajusterNiveau(niveau + 1)}
              disabled={niveau >= 500}
            >
              <i className="material-icons fs-6 d-flex align-items-center">add</i>
            </Button>
          </InputGroup>
        </div>

        <div className="d-flex gap-2 justify-content-center pt-2 border-top border-secondary border-opacity-20">
          <Button
            variant="success"
            size="sm"
            onClick={() => setEstActif(true)}
            disabled={estActif || tempsRestant === 0 || modeEdition}
            className="d-flex align-items-center gap-1 fw-semibold py-1 px-2"
          >
            <i className="material-icons fs-6">play_arrow</i> Go
          </Button>

          <Button
            variant="warning"
            size="sm"
            onClick={() => setEstActif(false)}
            disabled={!estActif || modeEdition}
            className="d-flex align-items-center gap-1 fw-semibold text-dark py-1 px-2"
          >
            <i className="material-icons fs-6">pause</i> Pause
          </Button>

          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => { setEstActif(false); setModeEdition(false); setTempsRestant(JOURS_EN_MS); }}
            disabled={modeEdition}
            className="d-flex align-items-center gap-1 text-white py-1 px-2"
          >
            <i className="material-icons fs-6">refresh</i> Reset
          </Button>
        </div>

      </Card.Body>
    </Card>
  );
}

export default function ComptesAReboursPersonnages() {
  const personnages = [
    { id: 1, nom: "Amphibio", icone: "🦎", variante: "success" },
    { id: 2, nom: "Bleck", icone: "🐺", variante: "info" },
    { id: 3, nom: "Heimdall", icone: "⚔️", variante: "warning" },
    { id: 4, nom: "Kelekil", icone: "🦅", variante: "danger" },
  ];

  return (
    <Container className="py-5 mb-5">
      <SidebarAdmin />
      <div className="admin-main-wrapper w-100">
        <div className="d-flex flex-column justify-content-center gap-0">
          <h1 className="text-center text-white fw-bold mb-4 display-4 text-uppercase" style={{ letterSpacing: '2px' }}>
            Total <span className="text-warning">Battle</span> {currentYear}
          </h1>
          <Row className="g-4">
            {personnages.map((perso) => (
              <Col key={perso.id} xs={12} md={6} lg={3}>
                <CartePersonnage id={perso.id} nom={perso.nom} icone={perso.icone} variante={perso.variante} />
              </Col>
            ))}
          </Row>
        </div>
      </div>
    </Container>
  );
}